// Tiny typed wrapper around the game's own backend API.
// `credentials: 'include'` keeps the httpOnly session cookie flowing.

export type User = {
  id: string;
  username: string;
  email: string;
  createdAt: string;
};

export type GameRecord = {
  id: string;
  mode: '2p' | 'vs-bot' | 'online';
  result: 'win' | 'loss' | 'draw';
  side: number;
  moves: unknown;
  playedAt: string;
};

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const init: RequestInit = {
    credentials: 'include',
    headers: options?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...options,
  };
  const res = await fetch(path, init);
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message = (data && (data.error || data.message)) || `Request failed (${res.status})`;
    throw new ApiError(res.status, message);
  }
  return data as T;
}

export const api = {
  me: () => request<{ user: User }>('/api/me').then((d) => d.user),
  signup: (username: string, email: string, password: string) =>
    request<{ user: User }>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    }).then((d) => d.user),
  login: (email: string, password: string) =>
    request<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }).then((d) => d.user),
  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
  games: () => request<{ games: GameRecord[] }>('/api/games').then((d) => d.games),
  saveGame: (payload: {
    mode: '2p' | 'vs-bot' | 'online';
    result: 'win' | 'loss' | 'draw';
    side: number;
    moves: unknown;
  }) =>
    request<{ game: GameRecord }>('/api/games', {
      method: 'POST',
      body: JSON.stringify(payload),
    }).then((d) => d.game),
};
